/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { fileService } from 'src/core/services/api/fileService';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { createBatchTestStore } from 'src/luis/core/stores/batchTestStore';
import { getBatchTestService } from 'src/luis/projects/testPane/services/batchTestService';
import {
    batchTestResultItemSwaggerTobatchTestResultItemStore,
    batchTestStoreToBatchTestSwagger
} from 'src/luis/projects/testPane/services/mappers';
import { BatchTestDataPointSwagger } from 'src/luis/projects/testPane/types/swagger/BatchTestDataPointSwagger';
import { getCorrectCount } from 'src/luis/projects/testPane/utils/batchTestResultsCalculator';
import { LuisId } from 'src/luis/types/Resource';
import { BatchTestStore } from 'src/luis/types/stores/testPane/BatchTestStore';

export type BatchTestHandlerGroup = ReturnType<typeof createBatchTestHandlerGroup>;

/**
 * Represents a handler group for batch test operations.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The handler group dependencies.
 */
export const createBatchTestHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const logger = getLogger();

    const getBatchTestStoreHouse = (appId: LuisId) =>
        rootStore.luisAppStoreHouse.findById(appId).currentAppVersionStore.batchTestStoreHouse;

    /**
     * Gets all batch tests available for the given app id.
     */
    const loadBatchTests = async ({ context }: HandlerParams<object>) => {
        const { appId } = context;
        const storeHouse = getBatchTestStoreHouse(appId);
        const logTimedActivity = logger.createTimedActivityLogger<BatchTestStore[]>('LoadBatchTests');

        storeHouse.setIsLoading(true);

        const batchTestStores = await logTimedActivity(getBatchTestService({ context }).getBatchTests());

        storeHouse.setItems(batchTestStores);
        storeHouse.setIsLoading(false);
        storeHouse.setIsLoaded(true);
    };

    /**
     * Adds batch test to the current batch test store house.
     *
     * @param param0.name The name of the test to add.
     * @param param0.datasetItems The dataset items of the test.
     */
    const addBatchTest = async ({
        context,
        name,
        datasetItemsFile
    }: HandlerParams<Pick<BatchTestStore, 'name'> & { datasetItemsFile: File }>) => {
        const { appId } = context;
        const storeHouse = getBatchTestStoreHouse(appId);
        const datasetItemsJson = await fileService.readFromDisk(datasetItemsFile);
        const datasetItems = <BatchTestDataPointSwagger[]>JSON.parse(datasetItemsJson);
        const logTimedActivity = logger.createTimedActivityLogger<string>('AddBatchTest');

        const testId = await logTimedActivity(getBatchTestService({ context }).addBatchTest({ testName: name, datasetItems }));
        const entitiesHouse = rootStore.luisAppStoreHouse.findById(appId).currentAppVersionStore.entityStoreHouse;
        const intentsHouse = rootStore.luisAppStoreHouse.findById(appId).currentAppVersionStore.intentStoreHouse;

        storeHouse.add(
            createBatchTestStore({
                name,
                id: testId,
                size: datasetItems.length,
                resultItems: datasetItems.map(datasetSwagger =>
                    batchTestResultItemSwaggerTobatchTestResultItemStore({ datasetSwagger, entitiesHouse, intentsHouse })
                )
            })
        );
    };

    /**
     * Deletes a test in current application.
     *
     * @param param0.testIds The ids of the tests to delete.
     */
    const deleteBatchTests = async ({ context, testIds }: HandlerParams<{ testIds: LuisId[] }>) => {
        const { appId } = context;
        const storeHouse = getBatchTestStoreHouse(appId);

        for (const id of testIds) {
            const logTimedActivity = logger.createTimedActivityLogger('DeleteBatchTests', () => ({ testId: id }));
            await logTimedActivity(getBatchTestService({ context }).deleteBatchTest({ testId: id }));
        }

        storeHouse.batchRemoveByIds(testIds);
    };

    /**
     * Renames a test in the current application.
     *
     * @param param0.newBatchTest The new test object to update.
     */
    const renameBatchTest = async ({ context, newBatchTest }: HandlerParams<{ newBatchTest: Partial<BatchTestStore> }>) => {
        const { appId } = context;
        const storeHouse = getBatchTestStoreHouse(appId);
        const logTimedActivity = logger.createTimedActivityLogger('RenameBatchTest', () => ({
            testId: newBatchTest.id,
            testName: newBatchTest.name
        }));

        const newBatchTestSwagger = batchTestStoreToBatchTestSwagger(newBatchTest);
        await logTimedActivity(getBatchTestService({ context }).renameBatchTest({ newBatchTest: newBatchTestSwagger }));

        storeHouse.findById(newBatchTest.id).rename(newBatchTest.name);
    };

    /**
     * Runs the test with the given id.
     *
     * @param param0.testId The id of the test to run.
     */
    const runBatchTest = async ({ testId, context }: HandlerParams<{ testId: LuisId }>) => {
        const { appId, versionId } = context;
        const storeHouse = getBatchTestStoreHouse(appId);
        const entitiesHouse = rootStore.luisAppStoreHouse.findById(appId).currentAppVersionStore.entityStoreHouse;
        const intentsHouse = rootStore.luisAppStoreHouse.findById(appId).currentAppVersionStore.intentStoreHouse;
        const batchTestStore = storeHouse.findById(testId);
        const batchTestService = getBatchTestService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<BatchTestDataPointSwagger[]>('RunBatchTest', () => ({ testId }));

        batchTestStore.setState('running');

        try {
            const datasetItemsSwagger = await logTimedActivity(batchTestService.runBatchTest({ appVersion: versionId, testId }));

            const datasetItems = datasetItemsSwagger.map(datasetSwagger =>
                batchTestResultItemSwaggerTobatchTestResultItemStore({ datasetSwagger, entitiesHouse, intentsHouse })
            );

            const correctCounts = getCorrectCount(datasetItems, entitiesHouse.items);

            transaction(() => {
                batchTestStore.setLastRunDate(new Date());
                batchTestStore.setLastResult(correctCounts);
                batchTestStore.setResultItems(datasetItems);
                batchTestStore.setState('success');
            });

            const newBatchTest = batchTestStoreToBatchTestSwagger(batchTestStore);
            await batchTestService.updateBatchTest({ newBatchTest });
        } catch (e) {
            transaction(() => {
                batchTestStore.setState('fail');
                batchTestStore.setErrorMessage(e.message);
            });
            throw e;
        }
    };

    /**
     * Exports the batch tests in the allowed file formats to be downloaded in the user device.
     *
     * @param param0.id The id of the test to export.
     * @param param0.name The name of the test to export.
     */
    const exportBatchTest = async ({ id, name, context }: HandlerParams<Pick<BatchTestStore, 'id' | 'name'>>) => {
        const logTimedActivity = logger.createTimedActivityLogger('ExportBatchTest', () => ({ testId: id, name }));

        await logTimedActivity(getBatchTestService({ context }).exportBatchTest({ testId: id, testName: `${name}.json` }));
    };

    return {
        loadBatchTests,
        addBatchTest,
        deleteBatchTests,
        renameBatchTest,
        runBatchTest,
        exportBatchTest
    };
};
